import { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { Button, TextInput } from '@forwardflow/ui-v2'
import { CheckCircle02Icon, ChevronRightIcon, VolumeMaxIcon } from '@forwardflow/icons'
import VoiceAvatar1 from '@forwardflow/assets/onboarding/voice1.png'
import VoiceAvatar2 from '@forwardflow/assets/onboarding/voice2.png'
import { Card, LinkButton, StepHeader } from '../shell/Card'
import { TVoiceOption } from '../steps/voice/voices'

const AVATARS = [VoiceAvatar1, VoiceAvatar2]

/**
 * The voice: our best-performing one is pre-selected, with Play, a name the
 * assistant introduces itself with, and the other voices one tap away.
 * The launch button lives here because this is the last thing we ask.
 */
export const VoicePick = ({
   voices,
   selectedId,
   name,
   onChange,
   onLaunch,
   launchLabel,
   launching,
   onBack,
}: {
   voices: TVoiceOption[]
   selectedId: string
   name: string
   onChange: (next: { voiceId: string; name: string }) => void
   onLaunch: () => void
   launchLabel: string
   launching?: boolean
   onBack: () => void
}) => {
   const [showAll, setShowAll] = useState(false)
   const shown = showAll ? voices : voices.slice(0, 2)
   const selected = voices.find((v) => v.voiceId === selectedId) || voices[0]

   const [playingId, setPlayingId] = useState<string | null>(null)
   const audioRef = useRef<HTMLAudioElement | null>(null)
   const stop = () => {
      if (audioRef.current) {
         audioRef.current.pause()
         audioRef.current.currentTime = 0
         audioRef.current = null
      }
      setPlayingId(null)
   }
   useEffect(() => stop, [])
   const play = (voice: TVoiceOption, e?: React.MouseEvent) => {
      e?.stopPropagation()
      if (playingId === voice.voiceId) {
         stop()
         return
      }
      stop()
      if (!voice.previewUrl) return
      const audio = new Audio(voice.previewUrl)
      audioRef.current = audio
      setPlayingId(voice.voiceId)
      audio.onended = () => setPlayingId(null)
      audio.onerror = () => setPlayingId(null)
      void audio.play().catch(() => setPlayingId(null))
   }

   const canLaunch = Boolean(selected) && name.trim().length > 0

   return (
      <div className="space-y-4">
         <Card>
            <StepHeader
               title="This is the voice your leads will hear"
               lede={`${voices[0]?.name || 'This voice'} books the most appointments across Forward Flow, so it is selected for you. Press play to hear it, change the name it introduces itself with, or pick another voice.`}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" role="radiogroup">
               {shown.map((voice, index) => {
                  const isSelected = selected?.voiceId === voice.voiceId
                  const isPlaying = playingId === voice.voiceId
                  return (
                     <button
                        key={voice.voiceId}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        data-testid={`voice-${index}`}
                        onClick={() => onChange({ voiceId: voice.voiceId, name })}
                        className={cn(
                           'rounded-xl border text-left p-3.5 flex items-center gap-3 transition-all',
                           isSelected
                              ? 'border-[#9F7BFF] bg-[#9F7BFF]/10'
                              : 'border-white/10 bg-white/[.02] hover:border-white/25'
                        )}
                     >
                        <div className="relative shrink-0">
                           <img
                              src={index < AVATARS.length ? AVATARS[index] : voice.avatar || AVATARS[index % AVATARS.length]}
                              alt={voice.name}
                              className={cn('w-14 h-14 rounded-full object-cover relative z-10 transition-transform', isPlaying && 'scale-105')}
                              onError={(e) => {
                                 const img = e.currentTarget as HTMLImageElement
                                 img.src = AVATARS[index % AVATARS.length]
                              }}
                           />
                           {isPlaying && (
                              <div className="absolute inset-0 rounded-full bg-[#9F7BFF] blur-xl opacity-60 animate-pulse pointer-events-none" />
                           )}
                        </div>
                        <div className="flex-1 min-w-0">
                           <p className="text-white font-semibold text-sm flex items-center gap-2">
                              {voice.name}
                              {index === 0 && (
                                 <span className="text-[10px] uppercase tracking-wide font-semibold rounded-full bg-[#9F7BFF]/25 text-[#C9B8FF] px-2 py-0.5">
                                    Best performing
                                 </span>
                              )}
                           </p>
                           {voice.description && (
                              <p className="text-[#99A1AF] text-xs mt-0.5 truncate">{voice.description}</p>
                           )}
                           <div className="flex items-center gap-3 mt-2">
                              <span
                                 role="button"
                                 tabIndex={0}
                                 className={cn(
                                    'rounded-lg inline-flex items-center gap-1.5 border px-2.5 py-1 text-xs text-white transition-all cursor-pointer hover:bg-white/[.08]',
                                    isPlaying ? 'border-[#9F7BFF] bg-[#9F7BFF]/20' : 'border-white/15 bg-white/[.02]',
                                    !voice.previewUrl && 'opacity-40 cursor-not-allowed'
                                 )}
                                 title={voice.previewUrl ? undefined : 'No preview for this voice'}
                                 onClick={(e) => play(voice, e)}
                                 onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                       e.preventDefault()
                                       play(voice)
                                    }
                                 }}
                              >
                                 <VolumeMaxIcon width={14} />
                                 {isPlaying ? 'Stop' : 'Play'}
                              </span>
                              {isSelected && (
                                 <span className="flex items-center gap-1 text-xs font-medium text-[#A684FF]">
                                    <CheckCircle02Icon width={14} height={14} /> Selected
                                 </span>
                              )}
                           </div>
                        </div>
                     </button>
                  )
               })}
            </div>

            {voices.length > 2 && (
               <LinkButton onClick={() => setShowAll((v) => !v)}>
                  {showAll ? 'Fewer voices' : `More voices (${voices.length - 2})`}
               </LinkButton>
            )}

            <TextInput
               size="sm"
               label="The name it introduces itself with"
               description="“Hi, this is Dakotah from Patel Realty.” Change it to anything you like."
               value={name}
               onChange={(e) => onChange({ voiceId: selected?.voiceId || '', name: e.currentTarget.value })}
               maxLength={40}
               data-testid="assistant-name"
            />
         </Card>

         <div className="flex items-center gap-3">
            <Button variant="secondary" size="middle" className="!h-11 !px-4" onClick={onBack} disabled={launching}>
               Back
            </Button>
            <Button
               size="middle"
               className="!h-11 flex-1"
               onClick={() => {
                  stop()
                  onLaunch()
               }}
               disabled={!canLaunch || launching}
               loading={launching}
               data-testid="start-calling"
            >
               {canLaunch ? launchLabel : 'Give the assistant a name'} <ChevronRightIcon width={16} />
            </Button>
         </div>
      </div>
   )
}
